// Requests waiting to be booked — the inbound half of the module.
//
// A doctor has asked for an operation and named a preferred day and time. They
// could not see the theatre diary when they asked, so their time is a wish;
// this is where somebody who CAN see it turns the wish into a booking.
//
// Accepting is one action that may also displace a scheduled case. Freeing a
// slot and dealing with the patient being moved are deliberately not two steps:
// the displaced patient may already be fasted and on the ward, and a flow that
// lets you free the slot first is a flow that lets you forget the second half.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";

import {
  IPD_COLORS,
  IPD_SHAPE,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
  ipdCardSx,
} from "../../ipd/utils";
import { formatIstDateTime, formatIstTime } from "../../../utils/istDateTime";
import {
  fetchOtRequests,
  fetchSchedule,
  fetchSetup,
  scheduleOtRequest,
  updateOtRequestTentativeCost,
} from "../api/otApi";
import { OtSelect, errorMessage, report } from "./common";

/** How far the booking sits from what was asked for, in words. */
function driftLabel(minutes) {
  if (minutes === null || minutes === undefined || minutes === 0) return null;
  const abs = Math.abs(minutes);
  const span =
    abs >= 60
      ? `${Math.floor(abs / 60)}h${abs % 60 ? String(abs % 60).padStart(2, "0") : ""}`
      : `${abs} min`;
  return `${span} ${minutes > 0 ? "later" : "earlier"} than requested slot`;
}

/**
 * Accept a request: pick the theatre and the real time, and if that slot is
 * taken, say what happens to whoever is in it.
 *
 * The bump section only appears once a clash is actually reported, so the
 * ordinary case — a free slot — is a three-field form.
 */
function ScheduleDialog({ request, rooms, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [clash, setClash] = useState("");
  const [dayCases, setDayCases] = useState([]);
  const [bump, setBump] = useState({ ot_case_id: "", action: "RESCHEDULE", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!request) return;
    // Pre-filled with what the doctor asked for. They could not see the diary,
    // so this is a starting point, not a commitment.
    const start = request.requested_datetime
      ? moment(request.requested_datetime)
      : moment().add(1, "day").hour(9).minute(0);
    setForm({
      operating_room_id: rooms[0]?.id ?? "",
      start: start.format("YYYY-MM-DDTHH:mm"),
      end: start
        .clone()
        .add(request.estimated_duration_min || 60, "minutes")
        .format("YYYY-MM-DDTHH:mm"),
      schedule_note: "",
    });
    setClash("");
    setBump({ ot_case_id: "", action: "RESCHEDULE", reason: "" });
  }, [request, rooms]);

  // What is already in THAT theatre on THAT day, so the bump picker offers
  // the cases actually in the way rather than asking for a case number from
  // memory. The schedule endpoint is the right source: it is the diary, and
  // it carries the patient on each booking. The cases list carries neither
  // a date filter nor the booking times.
  useEffect(() => {
    if (!clash || !form?.start || !form?.operating_room_id) return;
    fetchSchedule({ date: moment(form.start).format("YYYY-MM-DD") })
      .then((body) => {
        const room = (body?.rooms || []).find(
          (r) => String(r.id) === String(form.operating_room_id)
        );
        setDayCases(room?.bookings || []);
      })
      .catch(() => setDayCases([]));
  }, [clash, form?.start, form?.operating_room_id]);

  if (!request || !form) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        operating_room_id: form.operating_room_id,
        start_datetime: moment(form.start).toISOString(),
        end_datetime: moment(form.end).toISOString(),
        schedule_note: form.schedule_note || undefined,
      };
      if (bump.ot_case_id) {
        payload.bump = {
          ot_case_id: bump.ot_case_id,
          action: bump.action,
          reason: bump.reason,
          ...(bump.action === "RESCHEDULE"
            ? {
                operating_room_id: form.operating_room_id,
                start_datetime: moment(bump.start).toISOString(),
                end_datetime: moment(bump.end).toISOString(),
              }
            : {}),
        };
      }
      const res = await scheduleOtRequest(request.id, payload);
      if (res.ok) {
        report(res);
        onDone();
        return;
      }
      // A clash is not a dead end — it is the moment the bump becomes relevant,
      // so the message opens that section rather than just complaining.
      if (/already booked/i.test(res.message || "")) {
        setClash(res.message);
      } else {
        report(res);
      }
    } finally {
      setBusy(false);
    }
  };

  const bumpIncomplete =
    bump.ot_case_id &&
    (!bump.reason.trim() ||
      (bump.action === "RESCHEDULE" && (!bump.start || !bump.end)));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Schedule {request.patient_name}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            bgcolor: IPD_COLORS.pageBg,
            border: `1px solid ${IPD_COLORS.border}`,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {request.procedure_name}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            {request.surgeon_name || "Unassigned Primary Surgeon"} · {request.scheduling_type}
            {request.estimated_duration_min ? ` · ${request.estimated_duration_min} min` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
            Requested Slot:{" "}
            <strong>
              {request.requested_datetime
                ? formatIstDateTime(request.requested_datetime)
                : request.requested_date}
            </strong>
          </Typography>
          {request.notes && (
            <Typography sx={{ fontSize: 12.5, mt: 0.5, color: IPD_COLORS.warning.text }}>
              {request.notes}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "grid", gap: 2 }}>
          <IpdFormField label="Theatre">
            <OtSelect
              value={form.operating_room_id}
              onChange={(e) => setForm((f) => ({ ...f, operating_room_id: e.target.value }))}
              options={rooms.map((r) => ({ value: r.id, label: r.name }))}
            />
          </IpdFormField>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <IpdFormField label="Starts">
              <IpdTextField
                fullWidth
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
              />
            </IpdFormField>
            <IpdFormField label="Ends">
              <IpdTextField
                fullWidth
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
              />
            </IpdFormField>
          </Box>
          <IpdFormField label="Clinical Scheduling Rationale / Notes (optional)">
            <IpdTextField
              fullWidth
              placeholder="Clinical scheduling rationale or notes (e.g. emergency priority, surgeon request)..."
              value={form.schedule_note}
              onChange={(e) => setForm((f) => ({ ...f, schedule_note: e.target.value }))}
            />
          </IpdFormField>
        </Box>

        {clash && (
          <Box
            sx={{
              mt: 2,
              p: 1.75,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
              bgcolor: IPD_COLORS.error.bg,
              border: `1px solid ${IPD_COLORS.error.border}`,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: IPD_COLORS.error.text }}>
              {clash}
            </Typography>
            <Typography sx={{ fontSize: 12.5, mt: 0.5, mb: 1.5 }}>
              Select an alternative theatre/time slot, or reschedule the conflicting case to maintain continuous theatre scheduling.
            </Typography>

            <IpdFormField label="Conflicting Case to Reschedule">
              <OtSelect
                placeholder="Select conflicting case to reschedule..."
                value={bump.ot_case_id}
                onChange={(e) => setBump((b) => ({ ...b, ot_case_id: e.target.value }))}
                options={dayCases.map((b) => ({
                  value: b.ot_case_id,
                  label: `${b.ot_case?.patient_name || b.ot_case?.case_number} · ${formatIstTime(
                    b.start_datetime
                  )}–${formatIstTime(b.end_datetime)} · ${b.ot_case?.lifecycle_status}`,
                }))}
              />
            </IpdFormField>

            {bump.ot_case_id && (
              <Box sx={{ mt: 1.5, display: "grid", gap: 1.5 }}>
                <IpdFormField label="Displacement Action">
                  <OtSelect
                    value={bump.action}
                    onChange={(e) => setBump((b) => ({ ...b, action: e.target.value }))}
                    options={[
                      { value: "RESCHEDULE", label: "Reschedule to alternative slot today" },
                      { value: "POSTPONE", label: "Postpone case — defer to waiting list" },
                    ]}
                  />
                </IpdFormField>
                {bump.action === "RESCHEDULE" && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    <IpdFormField label="Revised Start Time">
                      <IpdTextField
                        fullWidth
                        type="datetime-local"
                        value={bump.start || ""}
                        onChange={(e) => setBump((b) => ({ ...b, start: e.target.value }))}
                      />
                    </IpdFormField>
                    <IpdFormField label="Revised End Time">
                      <IpdTextField
                        fullWidth
                        type="datetime-local"
                        value={bump.end || ""}
                        onChange={(e) => setBump((b) => ({ ...b, end: e.target.value }))}
                      />
                    </IpdFormField>
                  </Box>
                )}
                <IpdFormField label="Clinical Rescheduling Rationale">
                  <IpdTextField
                    fullWidth
                    value={bump.reason}
                    onChange={(e) => setBump((b) => ({ ...b, reason: e.target.value }))}
                  />
                </IpdFormField>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  Postponement will be logged under Theatre Rescheduling / Operational Deferrals in surgical quality and utilization metrics.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton disabled={busy || !form.operating_room_id || bumpIncomplete} onClick={submit}>
          {bump.ot_case_id ? "Reschedule Case & Confirm Booking" : "Confirm Booking"}
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

/** Check if a request has satisfied all pre-booking clearance gates. */
export function isRequestReady(r) {
  if (!r) return false;
  if ((r.scheduling_type || "").toUpperCase() === "EMERGENCY") return true;
  const pacCleared = (r.pac_status || "").toUpperCase() === "CLEARED";
  const billingCleared =
    (r.billing_status || "").toUpperCase() === "APPROVED" ||
    (r.billing_status || "").toUpperCase() === "NOT_REQUIRED";
  return pacCleared && billingCleared;
}

function TentativeCostDialog({ request, onClose, onDone }) {
  const [cost, setCost] = useState(
    request?.tentative_cost ?? request?.implant_details?.cost ?? ""
  );
  const [notes, setNotes] = useState(
    request?.implant_details?.cost_notes || ""
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (request) {
      setCost(request.tentative_cost ?? request.implant_details?.cost ?? "");
      setNotes(request.implant_details?.cost_notes || "");
    }
  }, [request]);

  if (!request) return null;

  const items = Array.isArray(request.implant_details?.items)
    ? request.implant_details.items
    : [];

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await updateOtRequestTentativeCost(request.id, {
        tentative_cost: Number(cost) || 0,
        notes: notes.trim(),
      });
      if (res?.ok || res?.status === "Success") {
        onDone();
      } else {
        report(res);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, bgcolor: "#1e293b", color: "#fff" }}>
        Implant Tentative Costing — Req #{request.id} ({request.patient_name})
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2, p: 2.5, bgcolor: "#f8fafc" }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          Set the estimated tentative cost for requested implants. This quotation figure is
          used by IPD Billing to verify financial clearance or advance deposit before theatre booking.
        </Typography>

        {items.length > 0 && (
          <Box sx={{ p: 1.5, bgcolor: "#fff", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, color: "#334155" }}>
              Doctor-Requested Implant Items ({items.length}):
            </Typography>
            {items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.5,
                  fontSize: 12.5,
                  borderBottom: idx < items.length - 1 ? "1px dashed #e2e8f0" : "none",
                }}
              >
                <span>• <strong>{item.item_name}</strong> {item.specifications ? `(${item.specifications})` : ""}</span>
                <span style={{ fontWeight: 600 }}>Qty: {item.quantity || 1}</span>
              </Box>
            ))}
          </Box>
        )}

        <IpdFormField label="Tentative Implant Cost (₹) *">
          <IpdTextField
            fullWidth
            type="number"
            placeholder="e.g. 45000"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />
        </IpdFormField>

        <IpdFormField label="Costing / Vendor Quotation Remarks">
          <IpdTextField
            fullWidth
            multiline
            rows={2}
            placeholder="Vendor details, quotation ref, package inclusions/exclusions…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </IpdFormField>
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: "#fff" }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <IpdButton onClick={handleSave} disabled={busy || cost === "" || Number(cost) < 0}>
          Save Tentative Cost
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The queue itself. Rendered on the OT dashboard above everything else, because
 * an unbooked request is the only thing on that screen nobody else is going to
 * pick up.
 */
export default function RequestQueue({ onScheduled }) {
  const [requests, setRequests] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [scheduling, setScheduling] = useState(null);
  const [costingRequest, setCostingRequest] = useState(null);
  const [tab, setTab] = useState("ready");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const body = await fetchOtRequests({ status: "PENDING" });
      setRequests(body?.requests || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the requests"));
    }
  }, []);

  useEffect(() => {
    load();
    fetchSetup()
      .then((s) => setRooms((s.operating_rooms || []).filter((r) => r.active !== false)))
      .catch(() => setRooms([]));
  }, [load]);

  if (error) {
    return (
      <IpdSectionCard title="Awaiting Theatre Scheduling">
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.error.text }}>{error}</Typography>
      </IpdSectionCard>
    );
  }

  // Nothing waiting is the normal state and does not need a card explaining it.
  if (requests.length === 0) return null;

  const readyRequests = requests.filter(isRequestReady);
  const pendingRequests = requests.filter((r) => !isRequestReady(r));

  const displayRequests =
    tab === "ready"
      ? readyRequests
      : tab === "pending"
      ? pendingRequests
      : requests;

  return (
    <>
      <IpdSectionCard
        title={`Theatre Booking & Scheduling Queue (${requests.length})`}
        subtitle="Pre-op clearance verification (PAC & Billing) and operating theatre allocation."
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ borderBottom: "1px solid #e2e8f0", mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, val) => setTab(val)}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                fontWeight: 700,
                fontSize: "0.8rem",
                textTransform: "none",
                minHeight: 40,
                py: 0.5,
              },
            }}
          >
            <Tab
              value="ready"
              label={`Ready for Booking (${readyRequests.length})`}
              sx={{ color: readyRequests.length > 0 ? "#166534" : undefined }}
            />
            <Tab
              value="pending"
              label={`Awaiting Clearance (${pendingRequests.length})`}
              sx={{ color: pendingRequests.length > 0 ? "#b45309" : undefined }}
            />
            <Tab value="all" label={`All Requests (${requests.length})`} />
          </Tabs>
        </Box>

        {displayRequests.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center", color: IPD_COLORS.textMuted }}>
            <Typography sx={{ fontSize: 13 }}>
              {tab === "ready"
                ? "No cases currently cleared for booking. Patients appear here once both PAC and Billing clearances are provided (or for emergency cases)."
                : tab === "pending"
                ? "No cases currently awaiting clearance."
                : "No pending surgical booking requests."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 1.25 }}>
            {displayRequests.map((r) => {
              const isReady = isRequestReady(r);
              const isEmergency = (r.scheduling_type || "").toUpperCase() === "EMERGENCY";
              const pacCleared = (r.pac_status || "").toUpperCase() === "CLEARED";
              const pacRejected = (r.pac_status || "").toUpperCase() === "REJECTED";
              const billingApproved = (r.billing_status || "").toUpperCase() === "APPROVED";
              const billingNotReq = (r.billing_status || "").toUpperCase() === "NOT_REQUIRED";
              const billingRejected = (r.billing_status || "").toUpperCase() === "REJECTED";
              const implantItems = Array.isArray(r.implant_details?.items)
                ? r.implant_details.items
                : [];

              return (
                <Box
                  key={r.id}
                  sx={{
                    ...ipdCardSx,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.2fr 1.2fr 1.3fr auto" },
                    alignItems: "center",
                    gap: 2,
                    p: 1.75,
                    borderLeft: `4px solid ${isReady ? "#22c55e" : "#f59e0b"}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                        {r.patient_name}
                      </Typography>
                      {isEmergency && (
                        <Chip
                          size="small"
                          label="EMERGENCY"
                          color="error"
                          sx={{ height: 18, fontSize: 10, fontWeight: 800 }}
                        />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                      {r.health_record_id || "MRN: Not Assigned"}
                      {r.age != null ? ` · ${r.age}y` : ""}
                      {r.gender ? ` / ${r.gender}` : ""}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted, mt: 0.25 }}>
                      Req #{r.id} · Requested: {formatIstDateTime(r.requested_at)}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                      {r.procedure_name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                      {r.surgeon_name || "Surgeon Unassigned"} · {r.scheduling_type}
                    </Typography>
                    {r.implant_required && (
                      <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            r.tentative_cost
                              ? `Implants: ₹${Number(r.tentative_cost).toLocaleString("en-IN")}`
                              : `Implants: Cost Pending (${implantItems.length} items)`
                          }
                          onClick={() => setCostingRequest(r)}
                          sx={{
                            height: 22,
                            fontSize: 10.5,
                            fontWeight: 700,
                            cursor: "pointer",
                            borderColor: r.tentative_cost ? "#0284c7" : "#f59e0b",
                            color: r.tentative_cost ? "#0284c7" : "#b45309",
                            bgcolor: r.tentative_cost ? "#f0f9ff" : "#fffbeb",
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Dual Gate Status */}
                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {/* PAC Gate */}
                      {pacCleared ? (
                        <Chip
                          size="small"
                          label="PAC: Cleared"
                          color="success"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                        />
                      ) : pacRejected ? (
                        <Chip
                          size="small"
                          label="PAC: Unfit / Hold"
                          color="error"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="PAC: Pending"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                        />
                      )}

                      {/* Billing Gate */}
                      {billingApproved ? (
                        <Chip
                          size="small"
                          label="Billing: Approved"
                          color="success"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                        />
                      ) : billingNotReq ? (
                        <Chip
                          size="small"
                          label="Billing: N/A"
                          sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }}
                        />
                      ) : billingRejected ? (
                        <Chip
                          size="small"
                          label="Billing: Rejected"
                          color="error"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Billing: Pending"
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }}
                        />
                      )}
                    </Stack>

                    <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                      Target: {r.requested_datetime ? formatIstDateTime(r.requested_datetime) : (r.requested_date || "Flexible / As Available")}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    {r.implant_required && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setCostingRequest(r)}
                        sx={{ fontSize: 11, minWidth: 80, textTransform: "none" }}
                      >
                        {r.tentative_cost ? "Edit Cost" : "Set Cost"}
                      </Button>
                    )}

                    <Tooltip
                      title={
                        isReady
                          ? "Both gates cleared. Ready for operating room booking."
                          : isEmergency
                          ? "Emergency surgery — pre-op gates bypassed."
                          : `Scheduling blocked: ${!pacCleared ? "Awaiting PAC clearance" : ""} ${!billingApproved && !billingNotReq ? (!pacCleared ? " & " : "") + "Awaiting Billing approval" : ""}`
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!isReady}
                          onClick={() => setScheduling(r)}
                          sx={{ minWidth: 88, textTransform: "none", fontWeight: 700 }}
                        >
                          Schedule
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </IpdSectionCard>

      <TentativeCostDialog
        request={costingRequest}
        onClose={() => setCostingRequest(null)}
        onDone={() => {
          setCostingRequest(null);
          load();
        }}
      />

      <ScheduleDialog
        request={scheduling}
        rooms={rooms}
        onClose={() => setScheduling(null)}
        onDone={() => {
          setScheduling(null);
          load();
          if (onScheduled) onScheduled();
        }}
      />
    </>
  );
}

export { driftLabel };
